import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../style/Second.css';
import backgroundImage from '../../images/background.png';
import slideImage2 from '../../images/second_slide_2.png';
import slideImage3 from '../../images/second_slide_3.png';
import slideImage4 from '../../images/second_slide_4.png';
import { getBanner } from '../../../api/getBanner';
import { API_URL } from '../../../config/connect';

function Second() {
    const { t } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [imageErrorById, setImageErrorById] = useState({});
    const [publicBannerUrl, setPublicBannerUrl] = useState("");

    const resolveBannerUrl = (url) => {
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        const normalizedApiUrl = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
        const normalizedPath = url.startsWith("/") ? url : `/${url}`;
        return `${normalizedApiUrl}${normalizedPath}`;
    };

    useEffect(() => {
        let cancelled = false;

        async function fetchPublicBanner() {
            try {
                const response = await getBanner();
                const bannerUrl =
                    response?.banner?.image_url ||
                    response?.banner?.background_image ||
                    response?.banner?.url ||
                    response?.image_url ||
                    response?.background_image ||
                    response?.banner_url ||
                    response?.url ||
                    "";

                if (!cancelled && bannerUrl) {
                    setPublicBannerUrl(resolveBannerUrl(bannerUrl));
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("Second: failed to fetch public banner", error);
                }
            }
        }

        fetchPublicBanner();
        return () => {
            cancelled = true;
        };
    }, []);

    const news = [
        {
            id: 1,
            titleKey: 'secondSlide1Title',
            descriptionKey: 'secondSlide1Description',
            backgroundImage: publicBannerUrl || backgroundImage
        },
        {
            id: 2,
            titleKey: 'secondSlide2Title',
            descriptionKey: 'secondSlide2Description',
            backgroundImage: slideImage2
        },
        {
            id: 3,
            titleKey: 'secondSlide3Title',
            descriptionKey: 'secondSlide3Description',
            backgroundImage: slideImage3
        },
        {
            id: 4,
            titleKey: 'secondSlide4Title',
            descriptionKey: 'secondSlide4Description',
            backgroundImage: slideImage4
        },
    ];

    // Автоматическое переключение слайдов
    useEffect(() => {
        if (!isAutoPlay) return;

        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % news.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isAutoPlay, news.length]);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % news.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);
    };

    const toggleAutoPlay = () => {
        setIsAutoPlay((prev) => !prev);
    };

    return (
        <div className="second_section relative">
            {/* Кнопка остановки/возобновления автопрокрутки */}
            <button
                onClick={toggleAutoPlay}
                className="absolute top-4 right-4 z-20 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                aria-label={isAutoPlay ? t('pauseSlideshow') : t('playSlideshow')}
            >
                {isAutoPlay ? (
                    <svg className="w-6 h-6 text-[#147F26]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                ) : (
                    <svg className="w-6 h-6 text-[#147F26]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )}
            </button>

            {/* Слайдер */}
            <div className="absolute inset-0 w-full h-full overflow-hidden">
                <div 
                    className="flex gap-0 transition-transform duration-500 ease-in-out h-full"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                    {news.map((item, index) => (
                        <div
                            key={item.id}
                            className="w-full h-full flex-shrink-0 relative"
                        >
                            <img
                                className="absolute inset-0 w-full h-full object-cover"
                                src={imageErrorById[item.id] ? backgroundImage : (item.backgroundImage || backgroundImage)}
                                alt=""
                                loading="lazy"
                                onError={() => {
                                    if (imageErrorById[item.id]) return;
                                    setImageErrorById((prev) => ({ ...prev, [item.id]: true }));
                                }}
                            />
                            <div className="absolute inset-0 bg-black/40"></div>
                            <div className="relative z-10 w-full h-full flex items-center">
                                <div className="w-full max-w-[1400px] mx-auto px-5 md:px-[60px] lg:px-[120px] xl:px-[240px]">
                                    <div className="max-w-2xl text-white">
                                        <h2 className="text-2xl font-bold mb-3">{t(item.titleKey)}</h2>
                                        <p className="text-base mb-4 opacity-90">{t(item.descriptionKey)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Кнопки навигации */}
            <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white rounded-full p-4 shadow-lg transition-all"
                aria-label={t('prevSlide')}
            >
                <svg className="w-6 h-6 text-[#147F26]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white rounded-full p-4 shadow-lg transition-all"
                aria-label={t('nextSlide')}
            >
                <svg className="w-6 h-6 text-[#147F26]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Индикаторы слайдов */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {news.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`h-2 rounded-full transition-all ${
                            index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
                        }`}
                        aria-label={t('goToSlide', { index: index + 1 })}
                    />
                ))}
            </div>
        </div>
    );
}

export default Second;